import * as Sentry from '@sentry/browser';

import { DEFAULT_SENTRY_CONFIG, SENTRY_DSN, SENTRY_PRODUCT, SENTRY_TEAM_NAME } from './constants';
import type { SentryClientConfig, SentryEventTags } from './types';

let sentryClient: Sentry.BrowserClient | undefined;

export function logException(exception: Error | unknown, location: string, tags?: SentryEventTags) {
	Sentry.withScope((scope) => {
		if (tags) {
			for (const [key, value] of Object.entries(tags)) {
				scope.setTag(key, String(value));
			}
		}

		// Required tags
		scope.setTag('location', location);
		scope.setTag('teamName', SENTRY_TEAM_NAME);
		sentryClient?.captureException(exception, undefined, scope);
	});
}

export function setupSentryClient({ sentryConfig }: { sentryConfig?: SentryClientConfig }) {
	if (!sentryClient) {
		const { dsn = SENTRY_DSN, product = SENTRY_PRODUCT, env } = sentryConfig || {};

		const productBuild = getProductBuild();
		const environment = getEnvironment(env);

		sentryClient = new Sentry.BrowserClient({
			...DEFAULT_SENTRY_CONFIG,
			dsn,
			environment,
			beforeSend: (event) => {
				// We only want errors captured explicitly via logException to avoid unwanted errors being sent
				if (event.tags?.teamName === undefined) {
					return null;
				}
				return event;
			},
			initialScope: {
				tags: {
					product,
					productBuild,
				},
			},
			attachStacktrace: true,
		});
	}
}

function getProductBuild() {
	// Ensure we don't invoke window object in SSR
	if (typeof window === 'undefined') {
		return 'unknown';
	}

	switch (true) {
		// Jira - window.BUILD_KEY
		case 'BUILD_KEY' in window:
			return (window as typeof window & { BUILD_KEY?: string }).BUILD_KEY || 'unknown';
		// Confluence - window.__buildInfo
		case '__buildInfo' in window:
			return (
				(window as typeof window & { __buildInfo?: { FRONTEND_VERSION?: string } }).__buildInfo
					?.FRONTEND_VERSION || 'unknown'
			);
		default:
			return 'unknown';
	}
}

function getEnvironment(env?: string) {
	// Ensure we don't invoke window object in SSR
	return typeof window !== 'undefined' && window.location?.href.includes('localhost')
		? 'dev'
		: (env ?? 'prod');
}
